import { Component, ChangeDetectionStrategy, signal, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { Router, RouterLink } from '@angular/router';
import { PERMISSION_UI_DIRECTIVES } from '../../directives/permission.directive';
import { SaleLinkDTO, SaleLinkService, SaleLinkStatus, SaleLinkType } from '../../services/sale-link.service';
import { CustomerDTO, CustomerService } from '../../services/customer.service';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { SkeletonModule } from 'primeng/skeleton';
import { FloatLabelModule } from 'primeng/floatlabel';
import { SelectModule } from 'primeng/select';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { TagModule } from 'primeng/tag';
import { DrawerModule } from 'primeng/drawer';
import { SaleLinkReadComponent } from './sale-link-read.component';
import { TooltipModule } from 'primeng/tooltip';
import { DialogModule } from 'primeng/dialog';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { DatePickerModule } from 'primeng/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { ToastrService } from 'ngx-toastr';

type LabeledCustomer = CustomerDTO & { displayLabel: string };

@Component({
  selector: 'app-sale-links-list',
  templateUrl: './sale-links-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        flex: 1 1 0%;
        min-height: 0;
        width: 100%;
      }
      :host ::ng-deep .sale-links-primeng-table .p-datatable-thead > tr > th {
        background: rgb(248 250 252);
        border-color: rgb(226 232 240);
        color: rgb(100 116 139);
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        padding: 0.55rem 0.5rem !important;
        position: sticky;
        top: 0;
        z-index: 20;
      }
      :host ::ng-deep .sale-links-primeng-table .p-datatable-thead > tr > th.p-sortable-column {
        cursor: pointer;
        user-select: none;
      }
      :host ::ng-deep .sale-links-primeng-table .p-datatable-thead > tr > th.p-sortable-column:hover {
        background: rgb(241 245 249);
        color: rgb(15 23 42);
      }
      :host ::ng-deep .sale-links-primeng-table .p-datatable-thead > tr > th.p-sortable-column.p-datatable-column-sorted {
        background: rgb(240 249 255);
        color: rgb(3 105 161);
      }
      :host ::ng-deep .sale-links-primeng-table .p-datatable-thead > tr > th.p-sortable-column .p-datatable-sort-icon,
      :host ::ng-deep .sale-links-primeng-table .p-datatable-thead > tr > th.p-sortable-column .p-sortable-column-icon,
      :host ::ng-deep .sale-links-primeng-table .p-datatable-thead > tr > th.p-sortable-column p-sorticon svg {
        width: 0.75rem;
        height: 0.75rem;
        color: rgb(14 165 233);
        opacity: 1;
        flex-shrink: 0;
        margin-left: auto;
      }
      :host ::ng-deep .sale-links-primeng-table .p-datatable-tbody > tr > td {
        padding: 0.4rem 0.45rem !important;
      }
      :host ::ng-deep .sale-links-primeng-table .p-datatable-table {
        width: 100%;
        table-layout: auto;
      }
      :host ::ng-deep p-table,
      :host ::ng-deep .sale-links-primeng-table.p-datatable,
      :host ::ng-deep .p-datatable {
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;
      }
      :host ::ng-deep .p-datatable-table-container,
      :host ::ng-deep .p-datatable-flex-scrollable > .p-datatable-table-container {
        flex: 1 1 0;
        min-height: 0;
        overflow: auto;
      }
      :host ::ng-deep .p-datatable-paginator-bottom {
        flex-shrink: 0;
      }
      :host ::ng-deep .p-paginator .p-paginator-page.p-paginator-page-selected,
      :host ::ng-deep button.p-paginator-page-selected {
        background: rgb(224 242 254) !important;
        border-color: rgb(14 165 233) !important;
        color: rgb(2 132 199) !important;
      }
      :host ::ng-deep .sale-link-details-drawer .p-drawer-header {
        background: rgb(241 245 249);
        border-bottom: 1px solid rgb(226 232 240);
        box-shadow: 0 1px 3px 0 rgb(15 23 42 / 0.06);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .sale-link-details-drawer .p-drawer-content {
        background: rgb(248 250 252);
        padding: 1rem 1.25rem;
      }
    `
  ],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterLink,
    PERMISSION_UI_DIRECTIVES,
    TableModule,
    ButtonModule,
    SkeletonModule,
    FloatLabelModule,
    SelectModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    TagModule,
    TooltipModule,
    DialogModule,
    AutoCompleteModule,
    DatePickerModule,
    DrawerModule,
    SaleLinkReadComponent,
    MatIconModule
  ]
})
export class SaleLinksListComponent {
  private saleLinkService = inject(SaleLinkService);
  private customerService = inject(CustomerService);
  private fb = inject(FormBuilder);
  private customerQuery = new Subject<string>();
  private router = inject(Router);
  private toastr = inject(ToastrService);

  links = signal<SaleLinkDTO[]>([]);
  loading = signal(true);
  initialLoadDone = signal(false);
  error = signal<string | null>(null);
  totalItems = signal(0);
  currentPage = signal(0);
  pageSize = signal(20);
  sortField = signal('createdAt');
  sortOrder = signal(-1);
  hasActiveFilters = signal(false);
  detailsOpen = signal(false);
  detailsLoading = signal(false);
  selectedLink = signal<SaleLinkDTO | null>(null);
  sendDialogOpen = signal(false);
  sendEmail = signal('');
  sending = signal(false);
  private sendLinkId: number | null = null;
  readonly pageSizeOptions = [10, 20, 50];
  readonly skeletonRows = [1, 2, 3, 4, 5];
  readonly statusOptions = [
    { label: 'Activa', value: 'ACTIVE' },
    { label: 'Pagada', value: 'PAID' },
    { label: 'Cancelada', value: 'CANCELLED' },
    { label: 'Vencida', value: 'EXPIRED' }
  ];
  readonly typeOptions = [
    { label: 'Pago único', value: 'SINGLE' },
    { label: 'Reutilizable', value: 'REUSABLE' },
    { label: 'Suscripción', value: 'SUBSCRIPTION' }
  ];
  customerOptions = signal<LabeledCustomer[]>([]);

  filtersForm = this.fb.group({
    type: '',
    customer: this.fb.control<LabeledCustomer | string | null>(null),
    serie: '',
    status: '',
    createdAtRange: this.fb.control<Date[] | null>(null)
  });

  constructor() {
    this.customerQuery.pipe(debounceTime(1000), distinctUntilChanged(), takeUntilDestroyed()).subscribe((query) => {
      this.customerService.listCustomers({
        'legalName[lk]': query,
        'comercialName[lk]': query,
        'taxId[lk]': query
      }, { page: 0, size: 20 }).subscribe((response) => {
        this.customerOptions.set((response.body ?? []).map((customer) => ({
          ...customer,
          displayLabel: customer.comercialName || customer.legalName || customer.taxId || ''
        })));
      });
    });
    this.filtersForm.controls.serie.valueChanges.pipe(
      debounceTime(1000),
      distinctUntilChanged(),
      takeUntilDestroyed()
    ).subscribe(() => this.applyFilters());
    this.filtersForm.controls.type.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => this.applyFilters());
    this.filtersForm.controls.status.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => this.applyFilters());
    this.filtersForm.controls.createdAtRange.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => this.applyFilters());
    this.load();
  }

  onCustomerSearch(event: { query: string }): void {
    const query = (event.query ?? '').trim();
    if (query.length < 3) {
      this.customerOptions.set([]);
      return;
    }
    this.customerQuery.next(query);
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    const raw = this.filtersForm.getRawValue();
    const spec: Record<string, string> = {};
    if (raw.type) {
      spec['type'] = raw.type;
    }
    const customer = this.selectedCustomer();
    if (customer?.id != null) {
      spec['customerId'] = String(customer.id);
    }
    const serie = raw.serie?.trim();
    if (serie) {
      spec['number[lk]'] = serie;
    }
    if (raw.status) {
      spec['status'] = raw.status;
    }
    this.applyCreatedAtRange(spec, raw.createdAtRange);
    const direction = this.sortOrder() === 1 ? 'asc' : 'desc';
    this.saleLinkService.list(spec, {
      page: this.currentPage(),
      size: this.pageSize(),
      sort: [`${this.sortField()},${direction}`]
    }).subscribe({
      next: (response) => {
        this.links.set(response.body ?? []);
        this.totalItems.set(response.page?.totalItems ?? response.body?.length ?? 0);
        this.loading.set(false);
        this.initialLoadDone.set(true);
      },
      error: () => {
        this.error.set('No se pudieron cargar las ligas de pago.');
        this.loading.set(false);
        this.initialLoadDone.set(true);
      }
    });
  }

  onLazyLoad(event: TableLazyLoadEvent): void {
    const size = event.rows || this.pageSize();
    const rawSortField = Array.isArray(event.sortField) ? event.sortField[0] : event.sortField;
    const nextSortField = rawSortField || this.sortField();
    const nextSortOrder = event.sortOrder ?? this.sortOrder();
    const sortChanged = nextSortField !== this.sortField() || nextSortOrder !== this.sortOrder();
    const page = sortChanged ? 0 : event.rows ? Math.floor((event.first ?? 0) / event.rows) : 0;
    if (!sortChanged && page === this.currentPage() && size === this.pageSize()) {
      return;
    }
    this.sortField.set(nextSortField);
    this.sortOrder.set(nextSortOrder);
    this.currentPage.set(page);
    this.pageSize.set(size);
    this.load();
  }

  applyFilters(): void {
    const raw = this.filtersForm.getRawValue();
    this.hasActiveFilters.set(!!(
      raw.type ||
      this.selectedCustomer() ||
      raw.serie?.trim() ||
      raw.status ||
      this.hasCompleteDateRange(raw.createdAtRange)
    ));
    this.currentPage.set(0);
    this.load();
  }

  clearFilters(): void {
    this.filtersForm.reset({
      type: '',
      customer: null,
      serie: '',
      status: '',
      createdAtRange: null
    }, { emitEvent: false });
    this.customerOptions.set([]);
    this.applyFilters();
  }

  private selectedCustomer(): LabeledCustomer | null {
    const customer = this.filtersForm.controls.customer.value;
    if (!customer || typeof customer === 'string' || customer.id == null) {
      return null;
    }
    return customer;
  }

  private hasCompleteDateRange(range: Date[] | null | undefined): boolean {
    return !!range?.[0] && !!range?.[1];
  }

  private applyCreatedAtRange(spec: Record<string, string>, range: Date[] | null | undefined): void {
    if (!this.hasCompleteDateRange(range) || !range?.[0] || !range[1]) {
      return;
    }
    const from = new Date(range[0]);
    from.setHours(0, 0, 0, 0);
    const toExclusive = new Date(range[1]);
    toExclusive.setHours(0, 0, 0, 0);
    toExclusive.setDate(toExclusive.getDate() + 1);
    spec['createdAt[gte]'] = from.toISOString();
    spec['createdAt[lt]'] = toExclusive.toISOString();
  }

  openDetails(link: SaleLinkDTO): void {
    if (!link?.id) return;
    this.detailsOpen.set(true);
    this.selectedLink.set(null);
    this.detailsLoading.set(true);
    this.saleLinkService.getById(link.id).subscribe({
      next: (response) => {
        this.detailsLoading.set(false);
        if (response.body) {
          this.selectedLink.set(response.body);
        } else {
          this.toastr.error('No se pudo cargar el detalle');
          this.closeDetails();
        }
      },
      error: () => {
        this.detailsLoading.set(false);
        this.toastr.error('No se pudo cargar el detalle');
        this.closeDetails();
      }
    });
  }

  closeDetails(): void {
    this.detailsOpen.set(false);
    this.selectedLink.set(null);
    this.detailsLoading.set(false);
  }

  onDrawerVisibleChange(visible: boolean): void {
    if (!visible) this.closeDetails();
  }

  editFromDrawer(): void {
    const link = this.selectedLink();
    if (!link?.id) return;
    this.closeDetails();
    void this.router.navigate(['/sale-links', link.id]);
  }

  edit(event: Event, link: SaleLinkDTO): void {
    event.stopPropagation();
    void this.router.navigate(['/sale-links', link.id]);
  }

  openSendDialog(event: Event, link: SaleLinkDTO): void {
    event.stopPropagation();
    if (link.status === 'PAID' || link.status === 'CANCELLED') {
      return;
    }
    this.sendLinkId = link.id;
    this.sendEmail.set(link.customerEmail ?? '');
    this.sendDialogOpen.set(true);
  }

  submitSend(): void {
    const email = this.sendEmail().trim();
    if (this.sendLinkId == null) {
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.toastr.error('El correo electrónico no es válido.');
      return;
    }
    this.sending.set(true);
    this.saleLinkService.send(this.sendLinkId, email).subscribe({
      next: () => {
        this.sending.set(false);
        this.sendDialogOpen.set(false);
        this.toastr.success('Liga de pago enviada');
      },
      error: (err) => {
        this.sending.set(false);
        this.toastr.error(err?.error?.message ?? 'No se pudo enviar la liga de pago');
      }
    });
  }

  showPaymentLink(event: Event, link: SaleLinkDTO): void {
    event.stopPropagation();
    const tab = window.open('about:blank', '_blank');
    this.saleLinkService.publicHash(link.id).subscribe({
      next: (response) => {
        if (!response.body) {
          tab?.close();
          this.toastr.error('No se pudo generar el link de pago');
          return;
        }
        const url = this.saleLinkService.publicPageUrl(link.id, response.body, link.type);
        if (tab) {
          tab.location.href = url;
        } else {
          window.open(url, '_blank', 'noopener,noreferrer');
        }
      },
      error: () => {
        tab?.close();
        this.toastr.error('No se pudo generar el link de pago');
      }
    });
  }

  typeLabel(type: SaleLinkType | undefined): string {
    if (type === 'REUSABLE') return 'Reutilizable';
    if (type === 'SUBSCRIPTION') return 'Suscripción';
    return 'Pago único';
  }

  statusLabel(status: SaleLinkStatus | undefined): string {
    return this.statusOptions.find((option) => option.value === status)?.label ?? status ?? '—';
  }

  statusSeverity(status: SaleLinkStatus | undefined): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    if (status === 'PAID') return 'success';
    if (status === 'ACTIVE') return 'info';
    if (status === 'EXPIRED') return 'warn';
    if (status === 'CANCELLED') return 'danger';
    return 'secondary';
  }

  statusIcon(status: SaleLinkStatus | undefined): string {
    if (status === 'PAID') return 'pi pi-check';
    if (status === 'CANCELLED') return 'pi pi-times';
    if (status === 'EXPIRED') return 'pi pi-clock';
    return 'pi pi-link';
  }

  statusIconClasses(status: SaleLinkStatus | undefined): string {
    const base = 'shrink-0 h-8 w-8 rounded-full flex items-center justify-center';
    if (status === 'PAID') return `${base} bg-emerald-100 text-emerald-600`;
    if (status === 'ACTIVE') return `${base} bg-sky-100 text-sky-600`;
    if (status === 'EXPIRED') return `${base} bg-amber-100 text-amber-600`;
    if (status === 'CANCELLED') return `${base} bg-slate-200 text-slate-600`;
    return `${base} bg-slate-100 text-slate-600`;
  }
}
